import axios, { AxiosError, AxiosHeaders, InternalAxiosRequestConfig } from "axios";
import { getToken, removeToken, getRefreshToken, saveToken, saveRefreshToken, isCookieAuthEnabled } from "@/shared/lib/token";
import { API_BASE_PATH, API_TIMEOUT_MS } from "@/shared/config";
import { ROUTES } from "@/shared/config";
import { getApiErrorCode, getApiErrorMessage } from "@/shared/lib/errorMessage";
import { getLocale, t } from "@/shared/i18n";
import { endSupportSession, getSupportSession } from "@/shared/lib/supportSession";
import { parseRetryAfterSeconds } from "@/shared/lib/retryAfter";
import { toPublicHref } from "@/shared/lib/router/publicHref";

let showToast: ((type: "success" | "error" | "warning" | "info", title: string, message: string) => void) | null = null;

export const registerToast = (fn: (type: "success" | "error" | "warning" | "info", title: string, message: string) => void) => {
    showToast = fn;
};

export const api = axios.create({
    baseURL: API_BASE_PATH,
    headers: {
    },
    timeout: API_TIMEOUT_MS,
    withCredentials: typeof window !== 'undefined' && !!(window as unknown as Record<string, unknown>).__TAURI_INTERNALS__ ? false : true,
});

export const publicApi = axios.create({
    baseURL: API_BASE_PATH,
    headers: {
    },
    timeout: API_TIMEOUT_MS,
});

/** Opções do client aceitas em qualquer chamada (api.get(url, { skipErrorToast: true })). */
declare module "axios" {
    interface AxiosRequestConfig {
        skipErrorToast?: boolean;
        skipAuth?: boolean;
    }
}

export interface CustomAxiosRequestConfig extends Partial<InternalAxiosRequestConfig> {
    _retry?: boolean;
    _retryCount?: number;
    skipErrorToast?: boolean;
    skipAuth?: boolean;
}

/** Para quem trata o erro na própria tela (ou o ignora): sem o toast global. */
export const SILENT_CONFIG: Readonly<CustomAxiosRequestConfig> = Object.freeze({ skipErrorToast: true });

type TokenRefreshResponse = {
    accessToken?: string;
    token?: string;
    refreshToken?: string;
    refresh_token?: string;
};

// Quem sabe renovar o token é entities/session, que já depende deste módulo.
// Registar de fora evita o ciclo que antes era contornado com import dinâmico.
let tokenRefresher: ((refreshToken?: string) => Promise<TokenRefreshResponse>) | null = null;

export function registerTokenRefresher(refresher: (refreshToken?: string) => Promise<TokenRefreshResponse>): void {
    tokenRefresher = refresher;
}

// Single-flight: garante UMA renovação concorrente em toda a app
// (interceptor de 401 + initAuth), evitando a corrida que invalida o
// refresh token quando o backend faz rotação (token de uso único).
let refreshPromise: Promise<string | null> | null = null;

export function refreshSession(): Promise<string | null> {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
        const refreshTokenValue = getRefreshToken();
        if (!refreshTokenValue && !isCookieAuthEnabled()) {
            throw new Error("No refresh token available");
        }

        if (!tokenRefresher) {
            throw new Error("Nenhum refresher de token registado");
        }
        const response = await tokenRefresher(refreshTokenValue || undefined);

        const accessToken = response.accessToken || response.token;
        // Leitura defensiva: aceita variações de nome do campo do refresh token.
        const newRefreshToken = response.refreshToken || response.refresh_token;

        if (accessToken) saveToken(accessToken);
        if (newRefreshToken) saveRefreshToken(newRefreshToken);

        return accessToken || null;
    })();

    // Libera o lock ao terminar (sucesso ou falha), sem engolir o erro para quem aguarda.
    refreshPromise.catch(() => { /* tratado por quem aguarda */ }).finally(() => {
        refreshPromise = null;
    });

    return refreshPromise;
}

/**
 * Propaga o locale activo para o backend. O `GlobalExceptionHandler` resolve
 * as mensagens das exceções `Localizable` via `MessageSource` +
 * `LocaleContextHolder`, que lê exactamente este header; sem ele o servidor
 * respondia sempre no locale por omissão enquanto a UI já estava noutro idioma.
 */
function applyLocaleHeader(config: InternalAxiosRequestConfig): void {
    const locale = getLocale();
    if (locale) {
        config.headers["Accept-Language"] = locale;
    }
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token && !config.url?.includes("/auth/refresh") && !(config as CustomAxiosRequestConfig).skipAuth) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    applyLocaleHeader(config);
    return config;
});

// A organização vem só do tenantId do token: o gateway apaga qualquer X-Tenant-Id que o cliente mande.
publicApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    applyLocaleHeader(config);
    return config;
});

function notify(type: "success" | "error" | "warning" | "info", title: string, message: string): void {
    if (!showToast) return;
    try {
        showToast(type, title, message);
    } catch { /* toast may not be ready */ }
}

/** Vários pedidos recusados juntos (ex.: um formulário que grava em lote) dão um toast só. */
const REPEATED_TOAST_WINDOW_MS = 5_000;
const lastToastAt = new Map<string, number>();

function notifyOnce(key: string, type: "success" | "error" | "warning" | "info", title: string, message: string): void {
    const now = Date.now();
    const last = lastToastAt.get(key);
    if (last !== undefined && now - last < REPEATED_TOAST_WINDOW_MS) return;
    lastToastAt.set(key, now);
    notify(type, title, message);
}

/** Sessão de suporte: o gateway responde `impersonation.forbidden`; o gestaodeobras, `auth.impersonation.readOnly`. */
const SUPPORT_READ_ONLY_CODES: ReadonlySet<string> = new Set(["impersonation.forbidden", "auth.impersonation.readOnly"]);

function withRetryAfter(message: string, error: AxiosError): string {
    const seconds = parseRetryAfterSeconds(error.response?.headers?.["retry-after"]);
    if (seconds === null) return message;
    return `${message} ${t("apiErrors.retryAfter", { count: seconds, defaultValue: "Tente novamente daqui a {{count}} s." })}`;
}

function redirectToLogin(): void {
    // Avoid infinite redirect loops if we are already on the login page
    if (typeof window !== "undefined" && !window.location.pathname.includes(ROUTES.login)) {
        window.location.href = toPublicHref(ROUTES.login);
    }
}

api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as CustomAxiosRequestConfig;

        if (!originalRequest) {
            return Promise.reject(error);
        }

        if (originalRequest.url?.includes("/auth/login") || originalRequest.url?.includes("/auth/refresh")) {
            return Promise.reject(error);
        }

        const status = error.response?.status;
        const code = getApiErrorCode(error);

        // Organização suspensa/cancelada, ou a pessoa suspensa nela: nenhuma chamada desta sessão vai passar.
        // O gateway responde só com o `code`; o header vem do gestaodeobras quando é chamado sem gateway.
        const lostOrganization = status === 403 && (
            code === "tenant.unavailable" ||
            code === "tenant.membership.suspended" ||
            error.response?.headers?.["x-tenant-unavailable"] === "true"
        );
        if (lostOrganization) {
            removeToken();
            notify(
                "warning",
                t("apiErrors.title.tenantUnavailable", { defaultValue: "Organização indisponível" }),
                getApiErrorMessage(error, t("apiErrors.tenantUnavailable", { defaultValue: "Esta organização não está disponível. Entre novamente." }))
            );
            redirectToLogin();
            return Promise.reject(error);
        }

        // Pedidos públicos (convite, OTP) não têm sessão para renovar: o 401 é a resposta.
        if (status === 401 && !originalRequest._retry && !originalRequest.skipAuth) {
            originalRequest._retry = true;

            // Sessão revogada: papéis mudaram, a pessoa foi desativada ou a senha foi trocada. O gateway
            // diz pelo `code`; o gestaodeobras, pelo header. O refresh resolve o primeiro caso; nos outros
            // ele falha e a pessoa volta ao login.
            const isSessionRevoked = code === "auth.session.revoked" || error.response?.headers?.["x-session-revoked"] === "true";

            // Renovação única e compartilhada: requests concorrentes que tomarem 401
            // aguardam a MESMA renovação (sem disparar vários /auth/refresh).
            try {
                const newToken = await refreshSession();

                originalRequest.headers = originalRequest.headers || new AxiosHeaders();
                if (newToken) {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                }
                return api(originalRequest);
            } catch (refreshError) {
                // O token de suporte não tem refresh: ao expirar, volta à sessão do admin.
                if (getSupportSession()) {
                    const returnUrl = endSupportSession();
                    if (typeof window !== "undefined" && returnUrl) {
                        window.location.href = returnUrl;
                    }
                    return Promise.reject(refreshError);
                }

                removeToken();
                if (isSessionRevoked) {
                    notify(
                        "warning",
                        t("apiErrors.title.sessionRevoked", { defaultValue: "Sessão Encerrada" }),
                        t("apiErrors.sessionRevoked", { defaultValue: "As suas permissões foram alteradas. Faça login novamente." })
                    );
                }
                redirectToLogin();
                return Promise.reject(refreshError);
            }
        }

        // Tratamento global de erros — UMA camada, não duas.
        //
        // Antes o interceptor notificava também os 4xx, e os `catch` dos
        // componentes voltavam a notificar via `getApiErrorMessage`, gerando
        // dois toasts para o mesmo erro. A divisão passa a ser:
        //   - interceptor: 5xx e falhas de rede/timeout (ninguém tem contexto
        //     útil para acrescentar, e o call-site pode nem existir);
        //   - call-site: 4xx (400/403/404/409/422...), onde só quem chamou sabe
        //     o que o utilizador estava a fazer.
        // Um 4xx que o call-site não trate fica silencioso por desenho: a
        // correção é tratar no `catch` com `getApiErrorMessage`.
        if (showToast && !originalRequest.skipErrorToast) {
            try {
                // Exceções ao "4xx é do call-site": nenhum call-site sabe tratar estes códigos do gateway.
                if (code && SUPPORT_READ_ONLY_CODES.has(code)) {
                    notifyOnce(
                        "support-read-only",
                        "warning",
                        t("apiErrors.title.supportReadOnly", { defaultValue: "Sessão de suporte" }),
                        t("apiErrors.supportReadOnly", { defaultValue: "A sessão de suporte é só de leitura: nada foi gravado." })
                    );
                } else if (code === "route.forbidden") {
                    notifyOnce("route-forbidden", "warning", t("apiErrors.title.forbidden", { defaultValue: "Sem permissão" }), getApiErrorMessage(error));
                } else if (code === "maintenance") {
                    notifyOnce(
                        "maintenance",
                        "warning",
                        t("apiErrors.title.maintenance", { defaultValue: "Em manutenção" }),
                        withRetryAfter(getApiErrorMessage(error), error)
                    );
                } else if (code === "module.disabled") {
                    showToast("warning", t("apiErrors.title.moduleDisabled", { defaultValue: "Módulo não ativo" }), getApiErrorMessage(error));
                } else if (code === "rate.limited") {
                    notifyOnce("rate-limited", "warning", t("apiErrors.title.rateLimited", { defaultValue: "Muitos pedidos" }), withRetryAfter(getApiErrorMessage(error), error));
                } else if (status === 503) {
                    notifyOnce("unavailable", "error", t("apiErrors.title.server", { defaultValue: "Erro no Servidor" }), withRetryAfter(getApiErrorMessage(error), error));
                } else if (status && status >= 500) {
                    showToast("error", t("apiErrors.title.server", { defaultValue: "Erro no Servidor" }), getApiErrorMessage(error));
                } else if (!error.response) {
                    // Sem resposta: rede indisponível ou timeout. O 401 já foi
                    // tratado acima (refresh/redirect) e nunca chega aqui sem response.
                    showToast("error", t("apiErrors.title.offline", { defaultValue: "Sem Ligação" }), getApiErrorMessage(error));
                }
            } catch (toastError) {
                // Silently fail if toast is not ready yet
                console.warn("Toast not ready:", toastError);
            }
        }



        return Promise.reject(error);
    }
);